import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./TeamName.module.css";

/** The separator between the levels of a team's path, as the server writes it. */
const PathSeparator = " › ";

/**
 * A team's name, with where it sits (its Division, Department and parent teams) in a tooltip, so the line stays short but the team can still
 * be told from others of the same name. The path is also the name's description for a screen reader, and the name can be tabbed to.
 *
 * @param props.path - Where the team sits, outermost first, ending in its name; empty when it was not recorded.
 * @param props.name - The team's name, used when there is no path.
 */
export function TeamName({
  path,
  name,
}: {
  path: string;
  name: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const tipId = useId();
  // Escape closes the tooltip even while the pointer rests on it (WCAG 1.4.13); it can open again once the pointer leaves.
  const [dismissed, setDismissed] = useState(false);
  const levels = path.split(PathSeparator).filter((level) => level !== "");
  const above = levels.slice(0, -1);
  const own = levels.length > 0 ? levels[levels.length - 1] : name;
  if (above.length === 0) {
    return <strong>{own}</strong>;
  }

  return (
    <span
      className={
        dismissed ? `${styles.anchor} ${styles.dismissed}` : styles.anchor
      }
      onMouseLeave={() => setDismissed(false)}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          setDismissed(true);
        }
      }}
    >
      <strong
        className={styles.name}
        tabIndex={0}
        aria-describedby={tipId}
        onBlur={() => setDismissed(false)}
      >
        {own}
      </strong>
      <span id={tipId} role="tooltip" className={`chart-tooltip ${styles.tip}`}>
        <span className={styles.label}>
          {t("requests.history.details.teamSits", "Sits under")}
        </span>
        {above.join(PathSeparator)}
      </span>
    </span>
  );
}
